import { combine, createEffect, createEvent, createStore } from 'effector';
import dayjs from 'dayjs';
import { $notificationStore } from '../notificationStore';
import { FileUtils } from '@shared/utils';
import {
  ReportApi,
  type FileResponse,
  type GenerateCardAccountBalanceReportRequest,
  type GeneratePaymentAccountBalanceReportRequest,
  type GeneratePaymentReportRequest,
  type GenerateTransactionReportRequest,
} from '@shared/api';

const generatePaymentReportEffect = createEffect((request: GeneratePaymentReportRequest) => ReportApi.generatePaymentReport(request));
const generateCardAccountBalanceReportEffect = createEffect((request: GenerateCardAccountBalanceReportRequest) =>
  ReportApi.generateCardAccountBalanceReport(request),
);
const generatePaymentAccountBalanceReportEffect = createEffect((request: GeneratePaymentAccountBalanceReportRequest) =>
  ReportApi.generatePaymentAccountBalanceReport(request),
);
const generateTransactionReportEffect = createEffect((request: GenerateTransactionReportRequest) => ReportApi.generateTransactionReport(request));
const clearEvent = createEvent();

const $dataStore = createStore<FileResponse | null>(null)
  .on(generatePaymentReportEffect.done, (_, data) => data.result)
  .on(generateCardAccountBalanceReportEffect.done, (_, data) => data.result)
  .on(generatePaymentAccountBalanceReportEffect.done, (_, data) => data.result)
  .on(generateTransactionReportEffect.done, (_, data) => data.result)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(generatePaymentReportEffect.pending, (_, data) => data)
  .on(generatePaymentReportEffect.fail, () => false)
  .on(generatePaymentReportEffect.done, () => false)
  .on(generateCardAccountBalanceReportEffect.pending, (_, data) => data)
  .on(generateCardAccountBalanceReportEffect.fail, () => false)
  .on(generateCardAccountBalanceReportEffect.done, () => false)
  .on(generatePaymentAccountBalanceReportEffect.pending, (_, data) => data)
  .on(generatePaymentAccountBalanceReportEffect.fail, () => false)
  .on(generatePaymentAccountBalanceReportEffect.done, () => false)
  .on(generateTransactionReportEffect.pending, (_, data) => data)
  .on(generateTransactionReportEffect.fail, () => false)
  .on(generateTransactionReportEffect.done, () => false)
  .reset(clearEvent);

generatePaymentReportEffect.done.watch((result) =>
  FileUtils.downloadFile(result.result, `Payments Report - ${dayjs().format('YYYY-MM-DDTHH_mm_ss')}`),
);
generatePaymentAccountBalanceReportEffect.done.watch((result) =>
  FileUtils.downloadFile(result.result, `Account Balance Report - ${dayjs().format('YYYY-MM-DDTHH_mm_ss')}`),
);
generateTransactionReportEffect.done.watch((result) =>
  FileUtils.downloadFile(result.result, `Transaction Report - ${dayjs().format('YYYY-MM-DDTHH_mm_ss')}`),
);
generateCardAccountBalanceReportEffect.done.watch((result) =>
  FileUtils.downloadFile(result.result, `Account Balance Report - ${dayjs().format('YYYY-MM-DDTHH_mm_ss')}`),
);

generatePaymentReportEffect.fail.watch(() =>
  $notificationStore.events.setError('Something went wrong while downloading Payment Report. Please try again.'),
);
generateCardAccountBalanceReportEffect.fail.watch(() =>
  $notificationStore.events.setError('Something went wrong while downloading Card Account Balance Report. Please try again.'),
);
generatePaymentAccountBalanceReportEffect.fail.watch(() =>
  $notificationStore.events.setError('Something went wrong while downloading Payment Account Balance Report. Please try again.'),
);
generateTransactionReportEffect.fail.watch(() =>
  $notificationStore.events.setError('Something went wrong while downloading Transaction Report. Please try again.'),
);

export const $reportStore = {
  events: {
    generatePaymentReport: generatePaymentReportEffect,
    generateCardAccountBalanceReport: generateCardAccountBalanceReportEffect,
    generatePaymentAccountBalanceReport: generatePaymentAccountBalanceReportEffect,
    generateTransactionReport: generateTransactionReportEffect,
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    loading: $loadingStore,
  }),
};
